// Renderer - Handles Canvas 2D and WebGL rendering
export class Renderer {
    constructor(canvas, options = {}) {
        this.canvas = canvas;
        this.options = {
            useWebGL: options.useWebGL || false,
            antialias: options.antialias || true,
            alpha: options.alpha || false,
            ...options
        };
        
        this.ctx = null;
        this.gl = null;
        this.isWebGL = false;
        this.dpr = window.devicePixelRatio || 1;
        
        this.init();
    }

    init() {
        // Try WebGL first if requested
        if (this.options.useWebGL && this.checkWebGLSupport()) {
            this.initWebGL();
        } else {
            this.initCanvas2D();
        }
        
        this.setupHighDPI();
    }

    checkWebGLSupport() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            return !!gl;
        } catch (e) {
            return false;
        }
    }

    initWebGL() {
        try {
            this.gl = this.canvas.getContext('webgl', {
                antialias: this.options.antialias,
                alpha: this.options.alpha,
                preserveDrawingBuffer: true
            }) || this.canvas.getContext('experimental-webgl');
            
            if (this.gl) {
                this.isWebGL = true;
                this.setupWebGL();
                console.log('🎮 WebGL renderer initialized');
            } else {
                throw new Error('WebGL not supported');
            }
        } catch (error) {
            console.warn('WebGL initialization failed, falling back to Canvas 2D:', error);
            this.initCanvas2D();
        }
    }

    initCanvas2D() {
        this.ctx = this.canvas.getContext('2d', {
            antialias: this.options.antialias,
            alpha: this.options.alpha
        });
        
        if (this.ctx) {
            // Optimize for pixel art games
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';
            console.log('🎨 Canvas 2D renderer initialized');
        } else {
            throw new Error('Canvas 2D not supported');
        }
    }

    setupWebGL() {
        if (!this.gl) return;
        
        const gl = this.gl;
        
        // Set up basic WebGL state
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        
        // Set clear color to black
        gl.clearColor(0.0, 0.0, 0.0, 1.0);
    }

    setupHighDPI() {
        const rect = this.canvas.getBoundingClientRect();
        
        // Set canvas drawing buffer size
        this.canvas.width = rect.width * this.dpr;
        this.canvas.height = rect.height * this.dpr;
        
        // Scale context for high DPI displays
        if (this.ctx) {
            this.ctx.scale(this.dpr, this.dpr);
        }
        
        if (this.gl) {
            this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        }
    }

    handleResize() {
        this.setupHighDPI();
    }

    clear(color = '#000000') {
        if (this.isWebGL && this.gl) {
            this.clearWebGL(color);
        } else if (this.ctx) {
            this.clearCanvas2D(color);
        }
    }

    clearWebGL(color) {
        const gl = this.gl;
        const rgb = this.hexToRgb(color);
        
        gl.clearColor(rgb.r / 255, rgb.g / 255, rgb.b / 255, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    }

    clearCanvas2D(color) {
        const ctx = this.ctx;
        ctx.save();
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);
        ctx.restore();
    }

    drawImage(image, x, y, width, height, rotation = 0, opacity = 1) {
        if (this.isWebGL) {
            this.drawImageWebGL(image, x, y, width, height, rotation, opacity);
        } else {
            this.drawImageCanvas2D(image, x, y, width, height, rotation, opacity);
        }
    }

    drawImageCanvas2D(image, x, y, width, height, rotation, opacity) {
        const ctx = this.ctx;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        
        if (rotation !== 0) {
            ctx.translate(x + width / 2, y + height / 2);
            ctx.rotate(rotation);
            ctx.drawImage(image, -width / 2, -height / 2, width, height);
        } else {
            ctx.drawImage(image, x, y, width, height);
        }
        
        ctx.restore();
    }

    drawImageWebGL(image, x, y, width, height, rotation, opacity) {
        // WebGL image rendering would require shader setup
        // This is a simplified version - in production you'd use a proper sprite batching system
        console.warn('WebGL image rendering not fully implemented');
        
        // Fallback to Canvas 2D for now
        if (this.ctx) {
            this.drawImageCanvas2D(image, x, y, width, height, rotation, opacity);
        }
    }

    drawRect(x, y, width, height, color, opacity = 1) {
        if (this.isWebGL) {
            this.drawRectWebGL(x, y, width, height, color, opacity);
        } else {
            this.drawRectCanvas2D(x, y, width, height, color, opacity);
        }
    }

    drawRectCanvas2D(x, y, width, height, color, opacity) {
        const ctx = this.ctx;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.fillStyle = color;
        ctx.fillRect(x, y, width, height);
        ctx.restore();
    }

    drawRectWebGL(x, y, width, height, color, opacity) {
        // WebGL rectangle rendering would require proper geometry setup
        console.warn('WebGL rectangle rendering not fully implemented');
        
        // Fallback to Canvas 2D for now
        if (this.ctx) {
            this.drawRectCanvas2D(x, y, width, height, color, opacity);
        }
    }

    drawCircle(x, y, radius, color, opacity = 1) {
        if (this.isWebGL) {
            this.drawCircleWebGL(x, y, radius, color, opacity);
        } else {
            this.drawCircleCanvas2D(x, y, radius, color, opacity);
        }
    }

    drawCircleCanvas2D(x, y, radius, color, opacity) {
        const ctx = this.ctx;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    drawCircleWebGL(x, y, radius, color, opacity) {
        // WebGL circle rendering would require proper geometry setup
        console.warn('WebGL circle rendering not fully implemented');
        
        // Fallback to Canvas 2D for now
        if (this.ctx) {
            this.drawCircleCanvas2D(x, y, radius, color, opacity);
        }
    }

    drawText(text, x, y, font = '16px Arial', color = '#ffffff', alignment = 'left', opacity = 1) {
        if (this.isWebGL) {
            this.drawTextWebGL(text, x, y, font, color, alignment, opacity);
        } else {
            this.drawTextCanvas2D(text, x, y, font, color, alignment, opacity);
        }
    }

    drawTextCanvas2D(text, x, y, font, color, alignment, opacity) {
        const ctx = this.ctx;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.font = font;
        ctx.fillStyle = color;
        ctx.textAlign = alignment;
        ctx.fillText(text, x, y);
        ctx.restore();
    }

    drawTextWebGL(text, x, y, font, color, alignment, opacity) {
        // WebGL text rendering would require proper font rendering system
        console.warn('WebGL text rendering not fully implemented');
        
        // Fallback to Canvas 2D for now
        if (this.ctx) {
            this.drawTextCanvas2D(text, x, y, font, color, alignment, opacity);
        }
    }

    drawLine(x1, y1, x2, y2, color = '#ffffff', width = 1, opacity = 1) {
        if (this.isWebGL) {
            this.drawLineWebGL(x1, y1, x2, y2, color, width, opacity);
        } else {
            this.drawLineCanvas2D(x1, y1, x2, y2, color, width, opacity);
        }
    }

    drawLineCanvas2D(x1, y1, x2, y2, color, width, opacity) {
        const ctx = this.ctx;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.restore();
    }

    drawLineWebGL(x1, y1, x2, y2, color, width, opacity) {
        // WebGL line rendering would require proper geometry setup
        console.warn('WebGL line rendering not fully implemented');
        
        // Fallback to Canvas 2D for now
        if (this.ctx) {
            this.drawLineCanvas2D(x1, y1, x2, y2, color, width, opacity);
        }
    }

    // Utility methods
    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
    }

    rgbToHex(r, g, b) {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    }

    // Getters
    getCanvas() {
        return this.canvas;
    }

    getContext() {
        return this.ctx;
    }

    getWebGLContext() {
        return this.gl;
    }

    isUsingWebGL() {
        return this.isWebGL;
    }

    getWidth() {
        return this.canvas.width / this.dpr;
    }

    getHeight() {
        return this.canvas.height / this.dpr;
    }
}